import { motion } from "framer-motion";
import { Camera, Send, Wrench } from "lucide-react";

const STEPS = [
  {
    n: "1",
    icon: Camera,
    title: "Snap",
    body: "Grab your phone and take 2–5 photos of what needs fixing — cracked tile, chipped wall, dodgy tap, anything.",
    tint: "bg-primary text-primary-foreground",
    numColor: "text-white/15",
  },
  {
    n: "2",
    icon: Send,
    title: "Send",
    body: "Drop the photos into our form with a quick note. We'll come back within 24 hours with a fixed price — no site visits, no obligation.",
    tint: "bg-muted text-secondary",
    numColor: "text-secondary/10",
  },
  {
    n: "3",
    icon: Wrench,
    title: "Repair",
    body: "Approve the quote, we book a vetted, insured local tradie, and the repair gets done. Simple.",
    tint: "bg-secondary text-secondary-foreground grain-overlay",
    numColor: "text-white/10",
  },
];

export default function HowItWorks() {
  return (
    <section id="how" className="py-24 lg:py-32 border-b border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-widest text-primary mb-3">How it works</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-secondary leading-none">
            Three steps.<br />
            No <span className="text-primary">phone tag.</span>
          </h2>
          <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
            We built Snap Send Repair for people who just want the job done. No awkward pricing calls, no waiting a week for someone to swing by and quote.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 lg:gap-6" data-testid="how-grid">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className={`relative overflow-hidden rounded-sm p-8 lg:p-10 min-h-[320px] ${s.tint}`}
                data-testid={`how-step-${s.n}`}
              >
                <div
                  className={`watermark-num absolute -bottom-8 -right-4 text-[220px] ${s.numColor} pointer-events-none select-none`}
                  aria-hidden
                >
                  {s.n}
                </div>
                <div className="relative">
                  <Icon size={28} strokeWidth={2} />
                  <h3 className="font-display text-3xl font-bold mt-6">{s.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed opacity-90 max-w-xs">{s.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
